'use client';

import React, { useState, useEffect, useCallback } from 'react';
import type { View, Reservation, Seat } from './types';
import { MOCK_SEATS, SPACES } from './data/mock-data';

// Components
import { TopNavBar } from './components/shared/TopNavBar';
import { LoginPage } from './components/user/LoginPage';
import { CalendarView } from './components/user/CalendarView';
import { SpaceBookingTabs } from './components/user/SpaceBookingTabs';
import { ResourcesView } from './components/user/ResourcesView';
import { QnAView } from './components/user/QnAView';

// 날짜를 YYYY-MM-DD 형식으로 변환
function formatDateForDB(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// 토스트 컴포넌트
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'warning' | 'error'; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : type === 'warning' ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className={`fixed bottom-20 left-1/2 -translate-x-1/2 ${bgColor} text-white px-4 py-3 rounded-lg shadow-lg z-50 max-w-[90%] text-center`}
      style={{ animation: 'fadeIn 0.3s ease-out' }}>
      {message}
    </div>
  );
}

// 예약 타이머 컴포넌트
function ReservationTimer({
  reservation,
  onExpire
}: {
  reservation: Reservation;
  onExpire: (id: string) => void;
}) {
  const [timeLeft, setTimeLeft] = useState<number>(0);

  useEffect(() => {
    if (!reservation.reserved_at || reservation.status !== 'pending') return;

    const reservedTime = new Date(reservation.reserved_at).getTime();
    // 데모용: 실제 30분 대신 60초로 설정 (테스트 편의)
    const expireTime = reservedTime + 60 * 1000; // 60초

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expireTime - now) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 0) {
        onExpire(reservation.id);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [reservation, onExpire]);

  if (reservation.status !== 'pending') return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const isUrgent = timeLeft <= 30;

  return (
    <div className={`text-xs font-mono ${isUrgent ? 'text-red-600' : 'text-yellow-700'}`}
      style={{ animation: isUrgent ? 'pulse 1s infinite' : 'none' }}>
      남은 시간: {minutes}:{seconds.toString().padStart(2, '0')}
    </div>
  );
}

export default function StudySpotDemo() {
  // 인증 상태 (데모용)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState<string | undefined>();
  const currentUserId = isGuest ? 'guest-user' : 'demo-user';

  // 뷰 상태
  const [currentView, setCurrentView] = useState<View>('main');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // 데이터 상태
  const [seats, setSeats] = useState<Seat[]>(MOCK_SEATS);
  const [reservations, setReservations] = useState<Reservation[]>([]);

  // 토스트 상태
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'warning' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'warning' | 'error' = 'success') => {
    setToast({ message, type });
  }, []);

  // 로그인 핸들러 (데모용)
  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setAuthError(undefined);

    await new Promise(resolve => setTimeout(resolve, 1000));

    if (email === 'demo@studyspot.com' && password === 'demo1234') {
      setIsAuthenticated(true);
    } else {
      setAuthError('이메일 또는 비밀번호가 올바르지 않습니다.');
    }
    setIsLoading(false);
  };

  // 비회원 로그인 핸들러
  const handleGuestLogin = () => {
    setIsGuest(true);
    setIsAuthenticated(true);
  };

  // 로그아웃 핸들러
  const handleLogout = () => {
    setIsAuthenticated(false);
    setIsGuest(false);
    setCurrentView('main');
    setReservations([]);
  };

  // 새로고침 핸들러
  const handleRefresh = () => {
    setSeats(MOCK_SEATS);
    setReservations([]);
    showToast('데이터가 초기화되었습니다.', 'success');
  };

  // 뷰 변경 핸들러
  const handleViewChange = (view: View) => {
    setCurrentView(view);
  };

  // 좌석 예약 핸들러
  const handleReserveSeat = (spaceId: string, seat: Seat) => {
    const dateString = formatDateForDB(selectedDate);
    const space = SPACES.find(s => s.id === spaceId);

    // 새 예약 생성
    const newReservation: Reservation = {
      id: `res-${Date.now()}`,
      user_id: currentUserId,
      nickname: isGuest ? '비회원' : '데모 사용자',
      is_anonymous: false,
      space_id: spaceId,
      seat_id: seat.id,
      seat_number: seat.number,
      reservation_date: dateString,
      time: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      status: 'pending',
      reserved_at: new Date().toISOString(),
    };

    // 좌석 상태 업데이트
    setSeats(prev => prev.map(s =>
      s.id === seat.id ? { ...s, status: 'my-reserved' as const, reserved_by: currentUserId } : s
    ));

    // 예약 추가
    setReservations(prev => [...prev, newReservation]);

    // 토스트 메시지
    showToast(
      `${space?.name} ${seat.number}번 자리 예약! 1분 내에 착석 확인해주세요.`,
      'warning'
    );
  };

  // 착석 확인 핸들러
  const handleConfirmSeat = (reservationId: string) => {
    setReservations(prev => prev.map(res =>
      res.id === reservationId ? { ...res, status: 'confirmed' as const } : res
    ));

    setSeats(prev => prev.map(s => {
      const reservation = reservations.find(r => r.id === reservationId);
      if (reservation && s.id === reservation.seat_id) {
        return { ...s, status: 'occupied' as const };
      }
      return s;
    }));

    showToast('착석이 확인되었습니다. 즐거운 학습 되세요!', 'success');
  };

  // 예약 취소 핸들러
  const handleCancelReservation = (reservationId: string) => {
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation) return;

    // 좌석 상태 복원
    setSeats(prev => prev.map(s =>
      s.id === reservation.seat_id ? { ...s, status: 'available' as const, reserved_by: undefined } : s
    ));

    // 예약 상태 변경
    setReservations(prev => prev.map(res =>
      res.id === reservationId ? { ...res, status: 'cancelled' as const } : res
    ));

    showToast('예약이 취소되었습니다.', 'success');
  };

  // 타이머 만료 핸들러 (자동 취소)
  const handleReservationExpire = useCallback((reservationId: string) => {
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation || reservation.status !== 'pending') return;

    // 좌석 상태 복원
    setSeats(prev => prev.map(s =>
      s.id === reservation.seat_id ? { ...s, status: 'available' as const, reserved_by: undefined } : s
    ));

    // 예약 상태 변경
    setReservations(prev => prev.map(res =>
      res.id === reservationId ? { ...res, status: 'cancelled' as const } : res
    ));

    showToast('시간 초과로 예약이 자동 취소되었습니다.', 'error');
  }, [reservations, showToast]);

  // 로그인 전
  if (!isAuthenticated) {
    return (
      <LoginPage
        onLogin={handleLogin}
        onGuestLogin={handleGuestLogin}
        isLoading={isLoading}
        error={authError}
      />
    );
  }

  // 현재 사용자의 pending 예약 찾기 (타이머 표시용)
  const pendingReservation = reservations.find(r => r.user_id === currentUserId && r.status === 'pending');

  // 로그인 후
  return (
    <div className="max-w-md mx-auto bg-gray-100 min-h-[600px]">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translate(-50%, 20px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>

      <TopNavBar
        currentView={currentView}
        onViewChange={handleViewChange}
        onLogout={handleLogout}
        onRefresh={handleRefresh}
      />

      {/* 예약 대기 중 타이머 배너 */}
      {pendingReservation && (
        <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-yellow-800 text-sm font-medium">
              {pendingReservation.seat_number}번 자리 예약 대기중
            </span>
          </div>
          <ReservationTimer
            reservation={pendingReservation}
            onExpire={handleReservationExpire}
          />
        </div>
      )}

      <div className="pb-8">
        {currentView === 'main' ? (
          <div className="space-y-4 p-4">
            <CalendarView
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
            />
            <SpaceBookingTabs
              selectedDate={selectedDate}
              seats={seats}
              reservations={reservations}
              onReserveSeat={handleReserveSeat}
              onConfirmSeat={handleConfirmSeat}
              onCancelReservation={handleCancelReservation}
              currentUserId={currentUserId}
            />
          </div>
        ) : currentView === 'resources' ? (
          <ResourcesView />
        ) : currentView === 'qna' ? (
          <QnAView />
        ) : null}
      </div>

      {/* 토스트 메시지 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
